import React, { useState } from "react"
import { motion } from "framer-motion"
import {Search, Edit, Trash2, Printer, FileText, Users} from 'lucide-react'
import { useGuias } from "../context/GuiasContext"
import jsPDF from "jspdf"
import autoTable from "jspdf-autotable"

interface ListarVendedoresProps {
  onEdit: (vendedorId: string) => void
}

export default function ListarVendedores({ onEdit }: ListarVendedoresProps) {
  const { vendedores, deleteVendedor, logoUrl, dadosPrefeitura } = useGuias()
  const [busca, setBusca] = useState("")
  const [vendedorSelecionado, setVendedorSelecionado] = useState<string | null>(null)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [vendedorToDelete, setVendedorToDelete] = useState<string | null>(null)

  const vendedoresFiltrados = vendedores.filter((v) =>
    v.nomeCompleto.toLowerCase().includes(busca.toLowerCase()) ||
    v.cpfCnpj.includes(busca) ||
    v.telefone.includes(busca)
  )

  const handleDelete = async (id: string) => {
    setVendedorToDelete(id)
    setShowDeleteModal(true)
  }

  const confirmarExclusao = async () => {
    if (vendedorToDelete) {
      await deleteVendedor(vendedorToDelete)
      setShowDeleteModal(false)
      setVendedorToDelete(null)
    }
  }

  const cancelarExclusao = () => {
    setShowDeleteModal(false)
    setVendedorToDelete(null)
  }

  const gerarRelatorioGeral = () => {
    const doc = new jsPDF()
    
    // Logo
    if (logoUrl) {
      doc.addImage(logoUrl, "PNG", 15, 10, 25, 25)
    }
    
    // Cabeçalho
    doc.setFontSize(16)
    doc.setFont("helvetica", "bold")
    doc.text("RELATÓRIO GERAL DE VENDEDORES", 105, 20, { align: "center" })
    
    doc.setFontSize(10)
    doc.setFont("helvetica", "normal")
    doc.text(dadosPrefeitura?.nomePrefeitura || "Prefeitura Municipal", 105, 28, { align: "center" })
    doc.text(`Data: ${new Date().toLocaleDateString("pt-BR")}`, 105, 34, { align: "center" })
    
    // Linha
    doc.setLineWidth(0.5)
    doc.line(15, 40, 195, 40)
    
    // Tabela
    const tableData = vendedoresFiltrados.map((v, index) => {
      const taxas = []
      if (v.taxaNossaSenhora) taxas.push(`N.Sra.Luz: ${v.valorTaxaNossaSenhora}`)
      if (v.taxaFestaSaoJose) taxas.push(`Festa S.José: ${v.valorTaxaFestaSaoJose}`)
      
      return [
        index + 1,
        v.nomeCompleto,
        v.cpfCnpj,
        v.telefone,
        v.ramosAtividade.join(", "),
        taxas.length > 0 ? taxas.join(", ") : "Nenhuma"
      ]
    })
    
    autoTable(doc, {
      head: [["#", "Nome", "CPF/CNPJ", "Telefone", "Ramos", "Taxas"]],
      body: tableData,
      startY: 45,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [255, 87, 34] },
      columnStyles: {
        5: { cellWidth: 35 }
      }
    })
    
    // Total
    const finalY = (doc as any).lastAutoTable.finalY
    doc.setFontSize(11)
    doc.setFont("helvetica", "bold")
    doc.text(`Total de Vendedores: ${vendedoresFiltrados.length}`, 15, finalY + 10)
    
    // Rodapé
    doc.setFontSize(8)
    doc.setFont("helvetica", "normal")
    doc.text("Desenvolvido por Railson Abreu", 105, 285, { align: "center" })
    
    doc.save("relatorio_vendedores.pdf")
  }

  const gerarRelatorioIndividual = (vendedor: any) => {
    const doc = new jsPDF()
    
    // Logo
    if (logoUrl) {
      doc.addImage(logoUrl, "PNG", 15, 10, 25, 25)
    }
    
    // Cabeçalho
    doc.setFontSize(16)
    doc.setFont("helvetica", "bold")
    doc.text("FICHA DE VENDEDOR", 105, 20, { align: "center" })
    
    doc.setFontSize(10)
    doc.setFont("helvetica", "normal")
    doc.text(dadosPrefeitura?.nomePrefeitura || "Prefeitura Municipal", 105, 28, { align: "center" })
    doc.text(`Data: ${new Date().toLocaleDateString("pt-BR")}`, 105, 34, { align: "center" })
    
    // Linha
    doc.setLineWidth(0.5)
    doc.line(15, 40, 195, 40)
    
    // Dados do vendedor
    let yPos = 55
    doc.setFontSize(12)
    doc.setFont("helvetica", "bold")
    doc.text("DADOS DO VENDEDOR", 15, yPos)
    
    yPos += 10
    doc.setFontSize(11)
    doc.setFont("helvetica", "bold")
    doc.text("Nome Completo:", 15, yPos)
    doc.setFont("helvetica", "normal")
    doc.text(vendedor.nomeCompleto, 60, yPos)
    
    yPos += 8
    doc.setFont("helvetica", "bold")
    doc.text("CPF/CNPJ:", 15, yPos)
    doc.setFont("helvetica", "normal")
    doc.text(vendedor.cpfCnpj, 60, yPos)
    
    yPos += 8
    doc.setFont("helvetica", "bold")
    doc.text("Telefone:", 15, yPos)
    doc.setFont("helvetica", "normal")
    doc.text(vendedor.telefone, 60, yPos)
    
    yPos += 12
    doc.setFont("helvetica", "bold")
    doc.text("Ramos de Atividade:", 15, yPos)
    yPos += 6
    doc.setFont("helvetica", "normal")
    vendedor.ramosAtividade.forEach((ramo: string) => {
      doc.text(`• ${ramo}`, 20, yPos)
      yPos += 6
    })
    
    yPos += 6
    doc.setFont("helvetica", "bold")
    doc.text("Taxas Aplicadas:", 15, yPos)
    yPos += 6
    doc.setFont("helvetica", "normal")
    if (vendedor.taxaNossaSenhora) {
      doc.text(`• Taxa Nossa Senhora da Luz: ${vendedor.valorTaxaNossaSenhora}`, 20, yPos)
      yPos += 6
    }
    if (vendedor.taxaFestaSaoJose) {
      doc.text(`• Taxa Festa de São José do Seridó: ${vendedor.valorTaxaFestaSaoJose}`, 20, yPos)
      yPos += 6
    }
    if (!vendedor.taxaNossaSenhora && !vendedor.taxaFestaSaoJose) {
      doc.text("• Nenhuma taxa aplicada", 20, yPos)
      yPos += 6
    }
    
    // Rodapé
    doc.setFontSize(8)
    doc.text("Desenvolvido por Railson Abreu", 105, 285, { align: "center" })
    
    doc.save(`ficha_vendedor_${vendedor.nomeCompleto.replace(/ /g, "_")}.pdf`)
  }

  const imprimirFicha = (vendedor: any) => {
    const printWindow = window.open("", "_blank")
    if (!printWindow) return

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Ficha de Vendedor - ${vendedor.nomeCompleto}</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { font-family: Arial, sans-serif; padding: 40px; }
            .header { text-align: center; margin-bottom: 30px; border-bottom: 2px solid #333; padding-bottom: 20px; }
            .logo { width: 80px; height: auto; margin-bottom: 10px; }
            h1 { font-size: 24px; color: #333; margin-bottom: 10px; }
            .info { margin-top: 30px; }
            .field { margin-bottom: 15px; }
            .field strong { display: inline-block; width: 150px; color: #555; }
            .ramos { margin-top: 20px; }
            .ramos ul { margin-left: 150px; list-style: disc; }
            .footer { margin-top: 50px; text-align: center; font-size: 12px; color: #666; }
            @media print {
              body { padding: 20px; }
              @page { margin: 1cm; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            ${logoUrl ? `<img src="${logoUrl}" alt="Logo" class="logo" />` : ""}
            <h1>FICHA DE VENDEDOR</h1>
            <p>${dadosPrefeitura?.nomePrefeitura || "Prefeitura Municipal"}</p>
            <p>Data: ${new Date().toLocaleDateString("pt-BR")}</p>
          </div>
          <div class="info">
            <div class="field"><strong>Nome Completo:</strong> ${vendedor.nomeCompleto}</div>
            <div class="field"><strong>CPF/CNPJ:</strong> ${vendedor.cpfCnpj}</div>
            <div class="field"><strong>Telefone:</strong> ${vendedor.telefone}</div>
            <div class="ramos">
              <strong>Ramos de Atividade:</strong>
              <ul>
                ${vendedor.ramosAtividade.map((ramo: string) => `<li>${ramo}</li>`).join("")}
              </ul>
            </div>
          </div>
          <div class="footer">
            <p>Desenvolvido por Railson Abreu</p>
          </div>
          <script>
            window.onload = () => {
              window.print()
              window.onafterprint = () => window.close()
            }
          </script>
        </body>
      </html>
    `)
    printWindow.document.close()
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg">
              <Users className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-800">Vendedores Cadastrados</h2>
              <p className="text-sm text-gray-600">{vendedoresFiltrados.length} vendedor(es) encontrado(s)</p>
            </div>
          </div>
          
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={gerarRelatorioGeral}
            className="flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
          >
            <FileText className="w-5 h-5" />
            Relatório Geral
          </motion.button>
        </div>

        <div className="mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Buscar por nome, CPF/CNPJ ou telefone..."
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            />
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nome</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">CPF/CNPJ</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Telefone</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ramos</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Taxas</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {vendedoresFiltrados.map((vendedor) => (
                <motion.tr
                  key={vendedor._id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="hover:bg-gray-50 transition-colors"
                >
                  <td className="px-4 py-3 text-sm text-gray-900">{vendedor.nomeCompleto}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{vendedor.cpfCnpj}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{vendedor.telefone}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    <div className="flex flex-wrap gap-1">
                      {vendedor.ramosAtividade.map((ramo, idx) => (
                        <span key={idx} className="px-2 py-1 bg-orange-100 text-orange-700 rounded text-xs">
                          {ramo}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm">
                    {vendedor.taxaNossaSenhora && (
                      <div className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded mb-1">
                        N. Sra. da Luz: {vendedor.valorTaxaNossaSenhora}
                      </div>
                    )}
                    {vendedor.taxaFestaSaoJose && (
                      <div className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                        Festa S. José: {vendedor.valorTaxaFestaSaoJose}
                      </div>
                    )}
                    {!vendedor.taxaNossaSenhora && !vendedor.taxaFestaSaoJose && (
                      <span className="text-gray-400 text-xs">Nenhuma</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-center gap-2">
                      <motion.button
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        onClick={() => imprimirFicha(vendedor)}
                        className="p-2 text-orange-600 hover:bg-orange-50 rounded-lg transition-colors"
                        title="Imprimir Ficha"
                      >
                        <Printer className="w-4 h-4" />
                      </motion.button>
                      <motion.button
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        onClick={() => gerarRelatorioIndividual(vendedor)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Gerar PDF"
                      >
                        <FileText className="w-4 h-4" />
                      </motion.button>
                      <motion.button
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        onClick={() => onEdit(vendedor._id!)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Editar"
                      >
                        <Edit className="w-4 h-4" />
                      </motion.button>
                      <motion.button
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        onClick={() => handleDelete(vendedor._id!)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Excluir"
                      >
                        <Trash2 className="w-4 h-4" />
                      </motion.button>
                    </div>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>

        {vendedoresFiltrados.length === 0 && (
          <div className="text-center py-12">
            <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">Nenhum vendedor encontrado</p>
          </div>
        )}
      </div>

      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl shadow-2xl p-6 max-w-md w-full mx-4"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 bg-red-100 rounded-lg">
                <Trash2 className="w-6 h-6 text-red-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-800">Confirmar Exclusão</h3>
            </div>
            <p className="text-gray-600 mb-6">
              Tem certeza que deseja excluir este vendedor? Esta ação não pode ser desfeita.
            </p>
            <div className="flex gap-3 justify-end">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={cancelarExclusao}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Cancelar
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={confirmarExclusao}
                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
              >
                Excluir
              </motion.button>
            </div>
          </motion.div>
        </div>
      )}
    </motion.div>
  )
}
